import React from 'react';
import PropTypes from 'prop-types';
import { FormGroup } from 'reactstrap';

import { useLanguageContext } from '../../../../_shared';

import { PRIVACY_TYPES } from '../../../constants';
import { formFieldShape } from '../../../utils';
import FormField from '../../FormField';
import PrivacyInput from '../../FormInput/PrivacyInput';

import './ProfileFormField.scss';

const propTypes = {

	/**
	 * The name of the organization.
	 */
	orgName: PropTypes.string.isRequired,

	/**
	 * The form field.
	 */
	field: formFieldShape.isRequired,

	/**
	 * The value for the form field.
	 */
	value: PropTypes.oneOfType([
		PropTypes.bool, // checkbox input
		PropTypes.number, // likert scale input
		PropTypes.arrayOf(PropTypes.string), // checkboxes input
		PropTypes.string, // all other inputs
	]),

	/**
	 * The privacy level for the form field.
	 *
	 * 0 = Only me, i.e. only me and org admins (DEPRECATED)
	 * 20 = Facilitators
	 * 40 = Classmates
	 * 60 = Public
	 */
	privacy: PropTypes.oneOf([
		PRIVACY_TYPES.ONLY_ME, // DEPRECATED
		PRIVACY_TYPES.FACILITATORS,
		PRIVACY_TYPES.CLASSMATES,
		PRIVACY_TYPES.PUBLIC,
	]),

	/**
	 * The error message (if any) for the form field.
	 */
	error: PropTypes.string,

	/**
	 * True, if the user is the editing the value for the form field.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the value for the form field is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * Callback to update the value for the form field.
	 */
	onChange: PropTypes.func,

};

/**
 * Component that handles the individual form fields for the user profile form.
 *
 * @param {Object} props The component props.
 *
 * @returns {Object} The ProfileFormField component.
 */
const ProfileFormField = (props) => {

	const {
		orgName,
		field,
		value,
		privacy,
		error,
		isEditing,
		isInvalid,
		onChange,
	} = props;

	const { chooseContentFromLanguage } = useLanguageContext();

	// Get the translated content for the form field
	const formFieldContent = chooseContentFromLanguage(field.form_field_contents);

	// Get the translated content for the form field options
	const options = field.form_field_options?.map((option) => {

		const formFieldOptionContent = chooseContentFromLanguage(
			option.form_field_option_contents
		);

		return {
			...option,
			label: formFieldOptionContent?.label || option.label,
		};

	});

	return (
		<div className="profile-form-field">
			<FormField
				type={field.type}
				name={field.id}
				value={value}
				options={options}
				label={formFieldContent?.title || field.title}
				placeholder={
					formFieldContent?.placeholder ||
					formFieldContent?.title ||
					field.placeholder ||
					field.title
				}
				help={formFieldContent?.help || field.help}
				autocomplete={field.type.toLowerCase()}
				error={error}
				isEditing={isEditing}
				isInvalid={isInvalid}
				isRequired={field.required}
				isHelpRichText
				onChange={onChange} />
			{isEditing &&
				<FormGroup>
					<PrivacyInput
						orgName={orgName}
						value={privacy}
						maxPrivacy={field.privacy}
						onChange={(newValue) => onChange(`${field.id}:privacy`, newValue)} />
				</FormGroup>
			}
		</div>
	);

};

ProfileFormField.propTypes = propTypes;

export default ProfileFormField;
